import { Input, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterRelationshipDetails } from '@creanizer/types'

interface RelationshipDetailsEditorProps {
  relationshipDetails: CharacterRelationshipDetails
  onChange: (relationshipDetails: CharacterRelationshipDetails) => void
}

export function RelationshipDetailsEditor({
  relationshipDetails,
  onChange
}: RelationshipDetailsEditorProps) {
  const [newNickname, setNewNickname] = useState({ name: '', nickname: '' })
  const [newRespected, setNewRespected] = useState('')
  const [newRival, setNewRival] = useState('')

  const handleAddNickname = () => {
    if (newNickname.name && newNickname.nickname) {
      onChange({
        ...relationshipDetails,
        nicknames: {
          ...(relationshipDetails.nicknames || {}),
          [newNickname.name]: newNickname.nickname
        }
      })
      setNewNickname({ name: '', nickname: '' })
    }
  }

  const handleRemoveNickname = (name: string) => {
    const { [name]: _, ...rest } = relationshipDetails.nicknames || {}
    onChange({ ...relationshipDetails, nicknames: rest })
  }

  const handleAddRespected = () => {
    if (newRespected) {
      onChange({
        ...relationshipDetails,
        respectedPeople: [...(relationshipDetails.respectedPeople || []), newRespected]
      })
      setNewRespected('')
    }
  }

  const handleAddRival = () => {
    if (newRival) {
      onChange({
        ...relationshipDetails,
        rivals: [...(relationshipDetails.rivals || []), newRival]
      })
      setNewRival('')
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <Label className="font-medium">他キャラクターへの呼び方</Label>
        <div className="space-y-2 mt-2">
          {Object.entries(relationshipDetails.nicknames || {}).map(([name, nickname]) => (
            <div key={name} className="flex gap-2 items-center">
              <span className="text-sm min-w-[80px]">{name}:</span>
              <Input value={nickname} disabled className="flex-1" />
              <Button size="sm" variant="ghost" onClick={() => handleRemoveNickname(name)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newNickname.name}
              onChange={(e) => setNewNickname(prev => ({ ...prev, name: e.target.value }))}
              placeholder="相手の名前"
              className="flex-1"
            />
            <Input
              value={newNickname.nickname}
              onChange={(e) => setNewNickname(prev => ({ ...prev, nickname: e.target.value }))}
              placeholder="呼び方"
              className="flex-1"
            />
            <Button size="sm" onClick={handleAddNickname}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">尊敬する人物</Label>
        <div className="space-y-2 mt-2">
          {(relationshipDetails.respectedPeople || []).map((person, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={person}
                onChange={(e) => {
                  const updated = [...(relationshipDetails.respectedPeople || [])]
                  updated[index] = e.target.value
                  onChange({ ...relationshipDetails, respectedPeople: updated })
                }}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onChange({
                    ...relationshipDetails,
                    respectedPeople: (relationshipDetails.respectedPeople || []).filter((_, i) => i !== index)
                  })
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newRespected}
              onChange={(e) => setNewRespected(e.target.value)}
              placeholder="例: 師匠、父親、歴史上の人物"
              onKeyPress={(e) => e.key === 'Enter' && handleAddRespected()}
              className="flex-1"
            />
            <Button size="sm" onClick={handleAddRespected}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">ライバル</Label>
        <div className="space-y-2 mt-2">
          {(relationshipDetails.rivals || []).map((rival, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={rival}
                onChange={(e) => {
                  const updated = [...(relationshipDetails.rivals || [])]
                  updated[index] = e.target.value
                  onChange({ ...relationshipDetails, rivals: updated })
                }}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onChange({
                    ...relationshipDetails,
                    rivals: (relationshipDetails.rivals || []).filter((_, i) => i !== index)
                  })
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newRival}
              onChange={(e) => setNewRival(e.target.value)}
              placeholder="例: 幼馴染、同期のライバル"
              onKeyPress={(e) => e.key === 'Enter' && handleAddRival()}
              className="flex-1"
            />
            <Button size="sm" onClick={handleAddRival}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
